import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { apiGet, apiPost, refreshAccessToken } from '../api';
import { unwrapData } from '@wapharma/shared';;

import { clearTokens, getToken, setAccessToken } from '../auth';
import { isAuthPublicPath } from '../auth/publicPaths';
import { getJwtExpiryMs } from '../utils/jwtExpiry';
import { ORG_SLUG } from '@wapharma/shared';

const USER_CACHE_KEY = 'hamssah_admin_user_snapshot';

function readUserCache(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

function writeUserCache(user: AuthUser | null) {
  try {
    if (!user) sessionStorage.removeItem(USER_CACHE_KEY);
    else sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
}

function tokenLooksUsable(token: string | null): boolean {
  if (!token) return false;
  const exp = getJwtExpiryMs(token);
  return exp == null || exp > Date.now();
}

function applyAuthUser(
  setUser: (u: AuthUser | null) => void,
  user: AuthUser | null,
) {
  setUser(user);
  writeUserCache(user);
}



export type OrganizationInfo = {
  id: string;
  slug: string;
  name: string;
  displayName: string | null;
  logoUrl: string | null;
  themeKey: string | null;
};

export type AuthUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureKey?: string | null;
  profilePictureUrl?: string | null;
  active: boolean;
  roles: string[];
  packs?: string[];
  activeOrgId?: string;
  organizations?: OrganizationInfo[];
  activeOrg?: OrganizationInfo | null;
  workspace?: 'hamssah-retail' | 'wapharma-ops';
};



type AuthState = {

  user: AuthUser | null;

  loading: boolean;

  mustChangePassword: boolean;

  login: (email: string, password: string) => Promise<void>;

  logout: () => Promise<void>;

  applyPasswordChange: (accessToken: string) => Promise<void>;

  refreshUser: () => Promise<boolean>;

};



const Ctx = createContext<AuthState | null>(null);



type LoginRes = {

  accessToken: string;

  email: string;

  roles: string[];

  mustChangePassword?: boolean;

};



type MeRes = {

  id: string;

  email: string;

  firstName?: string | null;

  lastName?: string | null;

  profilePictureKey?: string | null;

  profilePictureUrl?: string | null;

  active: boolean;

  roles: string[];

  packs?: string[];

  mustChangePassword?: boolean;

  activeOrgId?: string;

  organizations?: OrganizationInfo[];

  activeOrg?: OrganizationInfo | null;

  workspace?: 'hamssah-retail' | 'wapharma-ops';

};



export function AuthProvider({ children }: { children: ReactNode }) {

  const [user, setUser] = useState<AuthUser | null>(() => {
    const token = getToken();
    if (!tokenLooksUsable(token)) return null;
    return readUserCache();
  });

  const [mustChangePassword, setMustChangePassword] = useState(false);

  const [loading, setLoading] = useState(() => {
    const token = getToken();
    if (!tokenLooksUsable(token)) return false;
    return readUserCache() == null;
  });

  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);



  const refreshUser = useCallback(async (signal?: AbortSignal): Promise<boolean> => {

    if (refreshPromiseRef.current) {

      return refreshPromiseRef.current;

    }



    const task = (async (): Promise<boolean> => {

      let t = getToken();

      if (!t) {
        const onLogin =
          typeof window !== 'undefined' && isAuthPublicPath(window.location.pathname);

        if (!onLogin) {
          const refreshed = await refreshAccessToken();

          if (!refreshed) {
            applyAuthUser(setUser, null);
            setMustChangePassword(false);
            return false;
          }

          t = getToken();
        }
      }

      if (!t) {
        applyAuthUser(setUser, null);
        setMustChangePassword(false);
        return false;
      }

      const controller = new AbortController();

      const timeoutId = window.setTimeout(() => controller.abort(), 12_000);
      const onAbort = () => controller.abort();
      signal?.addEventListener('abort', onAbort);

      try {

        const raw = await apiGet<{ data: MeRes | null }>('/auth/me', t, controller.signal);

        const d = unwrapData(raw) as MeRes | null;

        if (!d) {

          applyAuthUser(setUser, null);

          setMustChangePassword(false);

          clearTokens();

          return false;

        }

        applyAuthUser(setUser, {
          id: d.id,
          email: d.email,
          firstName: d.firstName ?? null,
          lastName: d.lastName ?? null,
          profilePictureKey: d.profilePictureKey ?? null,
          profilePictureUrl: d.profilePictureUrl ?? null,
          active: d.active,
          roles: d.roles,
          packs: d.packs ?? [],
          activeOrgId: d.activeOrgId,
          organizations: d.organizations,
          activeOrg: d.activeOrg,
          workspace: d.workspace,
        });

        setMustChangePassword(Boolean(d.mustChangePassword));

        return true;

      } catch {

        if (controller.signal.aborted) return false;

        const keepStaleSession = readUserCache() != null;

        if (!keepStaleSession) {
          applyAuthUser(setUser, null);
          setMustChangePassword(false);
          clearTokens();
        }

        return keepStaleSession;

      } finally {

        window.clearTimeout(timeoutId);
        signal?.removeEventListener('abort', onAbort);

      }

    })();



    refreshPromiseRef.current = task;

    try {

      return await task;

    } finally {

      if (refreshPromiseRef.current === task) {

        refreshPromiseRef.current = null;

      }

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    let cancelled = false;
    const controller = new AbortController();

    const watchdog = window.setTimeout(() => {

      if (!cancelled) setLoading(false);

    }, 15_000);



    void refreshUser(controller.signal).finally(() => {

      cancelled = true;

      window.clearTimeout(watchdog);

    });



    return () => {

      cancelled = true;
      controller.abort();

      window.clearTimeout(watchdog);

    };

  }, [refreshUser]);



  const pinHamssahOrg = useCallback(async (): Promise<void> => {
    const t = getToken();
    if (!t) return;
    const raw = await apiGet<{ data: MeRes | null }>('/auth/me', t);
    const me = unwrapData(raw) as MeRes | null;
    const target = me?.organizations?.find((o) => o.slug === ORG_SLUG.HAMSSAH);
    if (!target || me?.activeOrgId === target.id) return;
    const switched = await apiPost<{ data: { accessToken: string } }>(
      '/auth/active-org',
      { legalEntityId: target.id },
      t,
    );
    setAccessToken(unwrapData<{ accessToken: string }>(switched).accessToken);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const raw = await apiPost<{ data: LoginRes }>('/auth/login', {
      email,
      password: password.trim(),
      client: 'admin',
    });

    const d = unwrapData(raw) as LoginRes;

    setAccessToken(d.accessToken);

    setMustChangePassword(Boolean(d.mustChangePassword));

    setLoading(true);

    await pinHamssahOrg();

    const ok = await refreshUser();

    if (!ok) {

      throw new Error(

        'Session invalide après connexion (GET /auth/me). Vérifiez l’API, CORS, et VITE_API_URL.',

      );

    }

  }, [refreshUser, pinHamssahOrg]);



  const applyPasswordChange = useCallback(async (accessToken: string) => {

    setAccessToken(accessToken);

    setMustChangePassword(false);

    setLoading(true);

    await pinHamssahOrg();

    await refreshUser();

  }, [refreshUser, pinHamssahOrg]);



  const logout = useCallback(async () => {

    try {

      await apiPost('/auth/logout', {});

    } catch {

      /* ignore */

    }

    clearTokens();

    applyAuthUser(setUser, null);

    setMustChangePassword(false);

    setLoading(false);

  }, []);



  const value = useMemo(
    () => ({
      user,
      loading,
      mustChangePassword,
      login,
      logout,
      applyPasswordChange,
      refreshUser,
    }),
    [user, loading, mustChangePassword, login, logout, applyPasswordChange, refreshUser],
  );



  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;

}



export function useAuth(): AuthState {

  const v = useContext(Ctx);

  if (!v) throw new Error('useAuth outside AuthProvider');

  return v;

}

